import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { TableRow, TableCell } from '@/components/ui/table'
import { Checkbox } from '../ui/Checkbox'
import { Users } from 'lucide-react'
import { RoomMember } from '@/redux/types/room'
import React from 'react'

interface PlayerAssignmentSectionProps {
  players: RoomMember[]
  teams: Array<{
    id: number
    name: string
  }>
  isPlayerAssignedToTeam: (playerId: string, teamId: number) => boolean
  onPlayerToggle: (teamId: number, playerId: string) => void
  isMobile?: boolean
  isReadOnly?: boolean
}

const PlayerAssignmentSection: React.FC<PlayerAssignmentSectionProps> =
  React.memo(
    ({
      players,
      teams,
      isPlayerAssignedToTeam,
      onPlayerToggle,
      isMobile = false,
      isReadOnly = false,
    }) => {
      if (isMobile) {
        return (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <span className='text-green-600 text-lg'>
                <Users />
              </span>
              <h3 className='text-lg font-semibold text-green-700'>Players</h3>
            </div>

            <div className='space-y-4'>
              {players.map((player) => (
                <Card
                  key={player.member_id}
                  className='hover:shadow-md transition-shadow'
                >
                  <CardHeader className='pb-3'>
                    <h4 className='font-semibold text-gray-800'>
                      {player.member_name || player.member_email}
                    </h4>
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`grid gap-3 ${
                        teams.length === 1
                          ? 'grid-cols-1'
                          : teams.length === 2
                            ? 'grid-cols-2'
                            : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
                      }`}
                    >
                      {teams.map((team) => (
                        <div
                          key={team.id}
                          className='flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100 hover:bg-green-100 transition-colors'
                        >
                          <span
                            className='font-medium text-sm text-gray-700 truncate mr-2'
                            title={team.name}
                          >
                            {team.name}
                          </span>
                          <Checkbox
                            checked={isPlayerAssignedToTeam(
                              player.member_id!,
                              team.id
                            )}
                            onChange={() =>
                              onPlayerToggle(team.id, player.member_id!)
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )
      }

      return (
        <>
          <TableRow className='bg-green-50'>
            <TableCell className='p-4 font-semibold text-green-700 border-r bg-gradient-to-r from-green-50 to-emerald-50 sticky left-0 z-10'>
              <div className='flex items-center gap-2'>
                <span className='text-green-600 text-lg'>
                  <Users />
                </span>
                <span>Players</span>
              </div>
            </TableCell>
            {teams.map((team) => (
              <TableCell
                key={team.id}
                className='p-4 bg-gradient-to-r from-green-50 to-emerald-50 whitespace-nowrap'
              />
            ))}
          </TableRow>

          {players.map((player) => (
            <TableRow
              key={player.member_id}
              className='border-t hover:bg-green-25 transition-colors'
            >
              <TableCell className='p-4 border-r sticky left-0 z-10 bg-white'>
                <div
                  className='font-medium text-gray-800 truncate'
                  title={player.member_name || player.member_email}
                >
                  {player.member_name || player.member_email}
                </div>
              </TableCell>
              {teams.map((team) => (
                <TableCell
                  key={team.id}
                  className='p-4 text-center align-middle whitespace-nowrap'
                >
                  <div className='flex justify-center items-center'>
                    <Checkbox
                      checked={isPlayerAssignedToTeam(
                        player.member_id!,
                        team.id
                      )}
                      onChange={() =>
                        onPlayerToggle(team.id, player.member_id!)
                      }
                      disabled={isReadOnly}
                    />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </>
      )
    }
  )

PlayerAssignmentSection.displayName = 'PlayerAssignmentSection'

export { PlayerAssignmentSection }
